import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Stack,
  Autocomplete,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import ProfilePhotoManager from './components/ProfilePhotoManager';
import { useAuth } from './contexts/AuthContext';
import { useFirebase } from './contexts/FirebaseContext';
import { useUserUtils } from './utilities/UserUtils';
const createSearchableString = (str) => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const FinishOnboarding = () => {
  const { userProfile, updateUserProfile, currentUser } = useAuth();
  const { db } = useFirebase();
  const [homeClub, setHomeClub] = useState('');
  const [clubSuggestions, setClubSuggestions] = useState([]);
  const navigate = useNavigate();
  const { checkUserNameAvailable, loading } = useUserUtils();
  const [userNameValidation, setUserNameValidation] = useState(false);

  // Use a ref to track if this is the initial load
  const isInitialLoad = useRef(true);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    homeClub: '',
    homeClubId: '',
  });

  // Initialize form data when userProfile is available
  useEffect(() => {
    if (userProfile && currentUser) {
      // Only set form data on initial load or if form is empty
      if (isInitialLoad.current) {
        // || (!formData.username && !formData.homeClub)) {
        setFormData({
          username: userProfile.username || '',
          email: currentUser.email || userProfile.email || '',
          homeClub: userProfile.homeClub || '',
          homeClubId: userProfile.homeClubId || '',
        });
        setHomeClub(userProfile.homeClub || '');
        isInitialLoad.current = false;
      }
    }
  }, [userProfile, currentUser]);

  const fetchClubSuggestions = useCallback(
    async (searchTerm) => {
      const clubsRef = collection(db, 'clubs');
      const searchableSearchTerm = createSearchableString(searchTerm);
      const clubsQuery = query(clubsRef, where('searchableClubName', '>=', searchableSearchTerm));
      const clubSnapshot = await getDocs(clubsQuery);
      const clubList = clubSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setClubSuggestions(clubList);
    },
    [db]
  );

  useEffect(() => {
    if (homeClub.length === 1) {
      fetchClubSuggestions(homeClub);
    }
  }, [homeClub, fetchClubSuggestions]);

  const handleClubChange = (val) => {
    if (val) {
      setFormData((prev) => ({
        ...prev,
        homeClub: val.clubName,
        homeClubId: val.id,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        homeClub: '',
        homeClubId: '',
      }));
    }
  };

  const handleUsernameChange = async (e) => {
    const { value } = e.target;
    handleInputChange(e);
    const usernameAvailable = await checkUserNameAvailable(value);
    setUserNameValidation(usernameAvailable);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {

      // Update profile fields in Firestore
      await updateUserProfile({
        username: formData.username,
        email: formData.email || currentUser.email,
        homeClub: formData.homeClub,
        homeClubId: formData.homeClubId,
        handicap: +formData.handicap,
      });

      navigate('/dashboard');
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  if (!userProfile) {
    return null;
  }

  return (
    <Paper
      sx={{
        width: '100%',
        maxWidth: '500px',
        padding: 4,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'transparent',
      }}
      variant="profile"
    >
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ mb: 4, fontWeight: 'bold', color: 'black' }}
      >
        Finish your Wagl Profile
      </Typography>

      <ProfilePhotoManager />

      <Stack spacing={3} sx={{ width: '100%', mt: 2 }}>
        <InputLabel htmlFor="username" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>Username { loading && <CircularProgress size={16} />}</InputLabel>
        <TextField
          fullWidth
          name="username"
          placeholder="Username"
          value={formData.username}
          onChange={handleUsernameChange}
          variant="outlined"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        {userNameValidation && !userNameValidation.valid && (
          <Typography variant="body2" sx={{ color: 'red', mt: 1 }}>
            {userNameValidation.message}
          </Typography>
        )}
        {userNameValidation &&
          !userNameValidation.valid &&
          userNameValidation.suggestedUserName && (
            <Typography variant="body2" sx={{ color: 'black', mt: 1 }}>
              Try:{' '}
              <strong
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    username: userNameValidation.suggestedUserName,
                  }));
                  handleUsernameChange({
                    target: { value: userNameValidation.suggestedUserName },
                  });
                }}
              >
                {userNameValidation.suggestedUserName}
              </strong>
            </Typography>
          )}
        <InputLabel htmlFor="username">First Name</InputLabel>
        <TextField
          fullWidth
          name="firstName"
          placeholder="First Name"
          value={formData.firstName}
          onChange={handleInputChange}
          variant="outlined"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <InputLabel htmlFor="lastName">Last Name</InputLabel>
        <TextField
          fullWidth
          name="lastName"
          placeholder="Last Name"
          value={formData.lastName}
          onChange={handleInputChange}
          variant="outlined"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        {!currentUser.email && (
          <>
            <InputLabel htmlFor="email">Email</InputLabel>
            <TextField
              fullWidth
              placeholder="Email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              variant="outlined"
              slotProps={{ inputLabel: { shrink: false } }}
            />
          </>
        )}
        <InputLabel htmlFor="club">Set your Home Club</InputLabel>
        <Autocomplete
          freeSolo
          disableClearable
          inputValue={homeClub}
          options={clubSuggestions}
          placeholder="Search for your home club"
          getOptionLabel={(option) => option.clubName}
          filterOptions={(options, { inputValue }) => {
            const normalizedInput = createSearchableString(inputValue);
            return options.filter((option) =>
              createSearchableString(option.searchableClubName).includes(normalizedInput)
            );
          }}
          onInputChange={(event, newInputValue) => setHomeClub(newInputValue)}
          onChange={(event, newValue) => handleClubChange(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              placeholder="Search for your home club"
              variant="outlined"
            />
          )}
        />
        <InputLabel htmlFor="club">What is your handicap?</InputLabel>
        <TextField
          type="number"
          fullWidth
          name="handicap"
          placeholder="Handicap"
          value={formData.handicap}
          onChange={handleInputChange}
          variant="outlined"
          slotProps={{ 
            inputLabel: { shrink: true },
            input: {
              inputProps: { min: 0.0, max: 54.0, step: 0.1 }
            }
          }}
          error={formData.handicap && (formData.handicap < 0.0 || formData.handicap > 54.0)}
          helperText={formData.handicap && (formData.handicap < 0.0 || formData.handicap > 54.0) ? "Handicap must be between 0.0 and 54.0" : ""}
        />
        <Button
          variant="containedWagl"
          color="primary"
          size="large"
          sx={{ mt: 2, py: 1.5 }}
          onClick={handleSubmit}
          disabled={!userNameValidation.valid}
        >
          Continue
        </Button>
      </Stack>
    </Paper>
  );
};

export default FinishOnboarding;
